import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../../lib/cn";
import { DialogOverlay } from "./dialog";

// shadcn-shaped Sheet primitive (task 799) — the side-anchored variant of
// Dialog, for the copilot dock's slide-in panel shape (M8b). Same Radix
// base as dialog.tsx; the pre-existing `.msg-panel` utility classes (used by
// MessageDrawer) stay untouched — this is the shadcn-idiom equivalent for
// new `components/ui/*` consumers.
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

const sheetVariants = cva(
  "fixed z-50 border-border bg-card shadow-lg flex flex-col",
  {
    variants: {
      side: {
        right: "inset-y-0 right-0 h-full w-full max-w-md border-l",
        left: "inset-y-0 left-0 h-full w-full max-w-md border-r",
        bottom: "inset-x-0 bottom-0 max-h-[80vh] border-t rounded-t-lg",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

export interface SheetContentProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof sheetVariants> {}

export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(
  function SheetContent({ className, side, children, ...props }, ref) {
    return (
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(sheetVariants({ side }), className)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);

export function SheetHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 border-b border-border p-4",
        className,
      )}
      {...props}
    />
  );
}

export const SheetTitle = forwardRef<
  HTMLHeadingElement,
  HTMLAttributes<HTMLHeadingElement>
>(function SheetTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-base font-semibold text-foreground", className)}
      {...props}
    />
  );
});

export const SheetDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function SheetDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
